// ════════════════════════════════════════════════════════════════════════
//  Lizenzzeitraum verkaufen
//
//  Das Formular rechnet nicht selbst. Ende und Betrag kommen aus lib/license.ts
//  und sind dort einzeln geprüft - beim letzten Mal blieb ein Test über das
//  gerenderte Bild grün, während die Anzeige falsch war.
//
//  Beide Vorschläge sind überschreibbar. Sie sind ein Vorschlag und keine
//  Regel: eine Kulanzverlängerung oder ein ausgehandelter Preis müssen ohne
//  Umweg eingebbar sein.
// ════════════════════════════════════════════════════════════════════════

import { useState } from "react";
import {
  formatEuro,
  nextAugust31,
  parseEuroToCents,
  proRatedCents,
  todayInBerlin,
} from "../lib/license.js";
import shell from "../../../shared/components/shell.module.css";
import styles from "./schools.module.css";

export function LicenseForm({
  annualPriceCents,
  currentLicenseUntil,
  pending,
  error,
  onSell,
}: {
  annualPriceCents: number | null;
  currentLicenseUntil: string | null;
  pending: boolean;
  error: string | null;
  onSell: (input: {
    validFrom: string;
    validUntil: string;
    priceCents: number | null;
    note: string | null;
  }) => void;
}) {
  // Anschluss statt Anfang: läuft schon eine Lizenz, beginnt der neue Zeitraum
  // an deren Ende. Sonst entsteht eine Lücke, und eine Lücke schließt man nicht
  // rückwirkend - der Löschjob hat dann schon gelöscht.
  const suggestedFrom =
    currentLicenseUntil !== null && currentLicenseUntil > todayInBerlin()
      ? currentLicenseUntil
      : todayInBerlin();

  const [from, setFrom] = useState(suggestedFrom);
  const [until, setUntil] = useState(nextAugust31(suggestedFrom));
  const [priceText, setPriceText] = useState("");
  const [note, setNote] = useState("");
  const [priceTouched, setPriceTouched] = useState(false);

  const suggestion = proRatedCents(annualPriceCents, from, until);
  // Solange niemand das Feld angefasst hat, gilt der Vorschlag. Ein
  // vorbelegtes Feld, das sich beim Ändern des Datums nicht mitbewegt, ist die
  // unangenehmere Variante: der Betrag stimmt dann sichtbar nicht zum Zeitraum.
  const effectivePrice = priceTouched ? parseEuroToCents(priceText) : suggestion;
  const priceBroken = priceTouched && priceText.trim() !== "" && effectivePrice === null;
  const rangeBroken = until <= from;

  const shownPrice = priceTouched
    ? priceText
    : suggestion === null
      ? ""
      : (suggestion / 100).toFixed(2).replace(".", ",");

  return (
    <div className={`${shell.card} ${shell.spaced}`}>
      <h2 className={shell.title} style={{ fontSize: "1.15rem" }}>
        Lizenzzeitraum verkaufen
      </h2>
      <p className={shell.subtitle}>
        Jeder Zeitraum endet am 31.08. – dem Ende des Schuljahres. Wer im Juli
        unterschreibt, hat sechs Wochen und zahlt anteilig; wer im September
        unterschreibt, das ganze Jahr.
      </p>

      {error && (
        <div className={`${shell.notice} ${shell.noticeError}`}>
          Nicht eingetragen: {error}
        </div>
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (rangeBroken || priceBroken) return;
          onSell({
            validFrom: from,
            validUntil: until,
            priceCents: effectivePrice,
            note: note.trim() === "" ? null : note.trim(),
          });
          setNote("");
          setPriceTouched(false);
        }}
      >
        <div className={styles.columns}>
          <div>
            <label htmlFor="valid-from">Beginn</label>
            <input
              id="valid-from"
              className={shell.field}
              type="date"
              value={from}
              onChange={(event) => {
                const value = event.target.value;
                setFrom(value);
                // Das Ende folgt dem Beginn, solange es der Vorschlag ist.
                if (value !== "") setUntil(nextAugust31(value));
              }}
              required
            />
          </div>
          <div>
            <label htmlFor="valid-until">Ende (nächster 31.08.)</label>
            <input
              id="valid-until"
              className={shell.field}
              type="date"
              value={until}
              onChange={(event) => setUntil(event.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="price">Betrag</label>
            <input
              id="price"
              className={shell.field}
              value={shownPrice}
              onChange={(event) => {
                setPriceTouched(true);
                setPriceText(event.target.value);
              }}
              placeholder={annualPriceCents === null ? "kein Jahrespreis" : "240,00"}
              inputMode="decimal"
            />
            <p className={styles.hint}>
              {annualPriceCents === null
                ? "Für diese Schule ist kein Jahrespreis eingetragen – der Betrag lässt sich nicht vorschlagen."
                : `Vorschlag: ${formatEuro(suggestion)} (Jahrespreis × Tage / 365)`}
            </p>
          </div>
        </div>

        <div className={shell.spaced}>
          <label htmlFor="license-note">Notiz zum Zeitraum</label>
          <input
            id="license-note"
            className={shell.field}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Erstes Jahr, Pilotschule"
          />
        </div>

        {rangeBroken && (
          <div className={`${shell.notice} ${shell.noticeError}`}>
            Das Ende muss nach dem Beginn liegen.
          </div>
        )}
        {priceBroken && (
          <div className={`${shell.notice} ${shell.noticeError}`}>
            Der Betrag muss eine Zahl sein, zum Beispiel 240,00.
          </div>
        )}

        <div className={styles.actions}>
          <button
            type="submit"
            className={shell.button}
            disabled={pending || rangeBroken || priceBroken}
          >
            {pending ? "Wird eingetragen …" : "Zeitraum eintragen"}
          </button>
        </div>
      </form>
    </div>
  );
}
